// @ts-nocheck
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { sectionActions } from 'ducks/section/actions';
import { useWidth } from 'utils/customHooks/index';
import RadioButton from 'components/RadioButton/index';
import InviteMembersInput from 'components/InviteMembersInput/index';
import NooderaDatePicker from 'components/NooderaDatePicker';
import NooderaButton from 'components/NooderaButton/index';
import NoodleTextField from 'components/NoodleTextField/index';
import NoodleTextArea from 'components/NoodleTextArea/index';
import SectionInformation from 'components/SectionInformation';
import LockIcon from '@mui/icons-material/Lock';
import LanguageIcon from '@mui/icons-material/Language';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CELLPHONE_LARGE_WIDTH } from 'commons/constants/size';
import { LMS } from 'commons/types/courseTypes';
import type { CreateSectionProps } from './CreateSection.types';
import {
  Container,
  Wrapper,
  StyledTitle,
  ChangeSectionTypeText,
  StyledLabel,
  FormItem,
  FieldWrapper,
  TitleWrapper,
  Title,
  HelpText,
  TitleContainer,
  SectionTitle,
  Section,
} from './CreateSection.styles';

const initialCounterValues = {
  name: 25,
  description: 140,
};

const CreateSection = ({
  dateSelected,
  courseSlug,
  unitId,
  moodleCourseId,
  onChangeSection,
  onChangeDate,
  headerTitle,
  onChangeHeaderTitle,
  onNextStep,
  onFinish,
  hasSections,
}: CreateSectionProps): React.ReactElement<any> | null => {
  const dispatch = useDispatch();
  const width = useWidth();
  const [data, setData] = React.useState<any>({
    inviteMembers: [],
    startDate: dateSelected
      ? moment(dateSelected).format('YYYY-MM-DD')
      : moment().format('YYYY-MM-DD'),
  });
  const [nameCounter, setNameCounter] = React.useState(initialCounterValues.name);
  const createSection = useSelector((state) => state.section?.createSection);
  const { data: coursePreview } = useSelector((state) => state.courses?.preview);
  const { data: globalPartner } = useSelector((state) => state.partnerInstance.globalPartner);
  const privacyItems = [
    {
      name: 'Public',
      value: 'public',
      subtitle:
        'Any learner can join your public team before the start date. You can also directly invite others to join.',
      linkText: '',
      icon: <LanguageIcon />,
    },
    {
      name: 'Private',
      value: 'private',
      subtitle: 'Only learners you invite, or approve, can join your team.',
      linkText: '',
      icon: <LockIcon />,
    },
  ];
  React.useEffect(() => {
    if (headerTitle?.value?.toLowerCase().includes('join')) {
      onChangeHeaderTitle();
    }

    return () => dispatch(sectionActions.createSectionReset());
  }, [headerTitle?.value]);
  React.useEffect(() => {
    if (createSection) {
      const { isLoading, success, error } = createSection;

      if (!isLoading && success && !error) {
        if (onFinish) {
          onFinish();
        }

        if (onNextStep) {
          onNextStep();
        }
      }
    }
  }, [createSection]);

  const calculateCounterValue = (initValue: number, value: number): number => {
    const result = initValue - value;
    return result > 0 ? result : 0;
  };

  const setCounter = (type: string, value: number): void => {
    if (type === 'name') {
      setNameCounter(value);
    }
  };

  const handleChangeSectionType = (): void => {
    onChangeSection('join');
  };

  const handleData = (key: string, value: string): void => {
    setData({ ...data, [key]: value });
  };

  React.useEffect(() => {
    if (dateSelected) {
      handleData('startDate', dateSelected);
    }
  }, [dateSelected]);

  const handleInputUpdate = (key: string, value: string): void => {
    const newCounterValue = calculateCounterValue(initialCounterValues[key], value.length);
    setCounter(key, newCounterValue);
    handleData(key, value);
  };

  const handleEmails = (email: string) => {
    const newObj = { ...data };
    newObj.inviteMembers = email;
    setData({ ...data, ...newObj });
  };

  const handleSubmit = (): void => {
    const payload = {
      ...data,
      brightspace_course_slug: courseSlug,
      visible: true,
      // TODO: We shouldn't be passing end_date
      end_date: '12-12-2022',
      image: '',
      partnerId: globalPartner.partnerId,
    };

    if (coursePreview.lms === LMS.Brightspace) {
      payload.brightspace_org_unit_id = unitId;
    } else {
      payload.moodle_course_id = moodleCourseId;
    }

    dispatch(sectionActions.createSectionRequest(payload));
  };

  const isButtonDisabled = (): boolean =>
    !(data?.description && data?.name && typeof data?.public === 'boolean' && data?.startDate);

  React.useEffect(() => {
    if (createSection?.success) {
      dispatch(sectionActions.createSectionReset());
    }
  }, [createSection?.success, data]);

  const getButtonText = (): string => {
    if (createSection?.isLoading) {
      return '...';
    }

    if (onFinish) {
      return 'Finish';
    }

    return 'Next';
  };

  return (
    <Wrapper>
      <Container>
        <TitleContainer>
          <StyledTitle>Create a Team</StyledTitle>
          {hasSections && (
            <ChangeSectionTypeText onClick={handleChangeSectionType}>
              Join Team
            </ChangeSectionTypeText>
          )}
        </TitleContainer>
        <SectionInformation
          icon={<InfoOutlinedIcon />}
          title='Why do I need a start date?'
          description="If you choose to create a public team, the start date helps other learners pick the cohort that works best for their schedule. Don't worry - you can edit your start date later on and we'll notify the rest of your cohort."
        />
        <Section>
          <SectionTitle>Start Date</SectionTitle>
          <NooderaDatePicker
            minDate={new Date()}
            disablePast
            value={dateSelected}
            variant={width > CELLPHONE_LARGE_WIDTH ? 'inline' : 'dialog'}
            onChange={(value) => onChangeDate(value)}
          />
        </Section>
        {/* <FormItem>
         <StartingDateInfo>
           2 other teams start that week.{' '}
           <ChangeSectionTypeSpan onClick={handleChangeSectionType}>
             Join them instead
           </ChangeSectionTypeSpan>
         </StartingDateInfo>
        </FormItem> */}
        <FormItem>
          <StyledLabel>Privacy</StyledLabel>
          <RadioButton.CustomContent
            name='Privacy'
            items={privacyItems}
            size='medium'
            onChange={(_, value) => {
              setData({ ...data, public: value === 'public' });
            }}
          />
        </FormItem>
        <FormItem>
          <StyledLabel>Details</StyledLabel>
          <FieldWrapper>
            <TitleWrapper>
              <Title>Name</Title>
            </TitleWrapper>
            <NoodleTextField
              inputProps={{
                maxLength: 25,
              }}
              name='name'
              value={data?.name}
              variant='outlined'
              autoComplete='off'
              placeholder='Give your team a name'
              data-testid='create-section-name'
              onChange={({ currentTarget: { value } }) => handleInputUpdate('name', value)}
            />
            <HelpText>{nameCounter} characters</HelpText>
          </FieldWrapper>
          <FieldWrapper>
            <TitleWrapper>
              <Title>About</Title>
            </TitleWrapper>
            <NoodleTextArea
              maxLength={140}
              placeholder='What are your goals for this learning experience? Write them here to help fill your team with likeminded learners.'
              data-testid='create-section-about'
              multiline
              name='about'
              variant='outlined'
              autoComplete='off'
              value={data?.description}
              rows={4}
              onChange={({ currentTarget: { value } }) => handleInputUpdate('description', value)}
            />
          </FieldWrapper>
        </FormItem>
        <FormItem>
          <StyledLabel>Invite Members</StyledLabel>
          <InviteMembersInput data={data?.inviteMembers} onChange={handleEmails} />
        </FormItem>
        <NooderaButton.CTA
          data-testid='finish-free-course-enrollment'
          isDisabled={createSection?.isLoading || isButtonDisabled()}
          onClick={handleSubmit}
        >
          {getButtonText()}
        </NooderaButton.CTA>
      </Container>
    </Wrapper>
  );
};

export default CreateSection;
